import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import { openSidePanel, SidePanel } from '@casops/store/slices/globalSlice';
import { ModelName } from '@casops/store/api';
import {
  SafetyAlertBasicModel,
  useListSafetyAlertsQuery,
} from '@casops/store/apis/personnelSafetyApi';
import SafetyAlertAcknowledgedCellRenderer, {
  getSafetyAlertStatusText,
} from '@casops/components/tables/cells/safety-alert-status/SafetyAlertStatusCellRenderer';
import { useAbsoluteTimePartialColDef } from '@casops/components/tables/cells/absolute-time/useAbsoluteTime';
import { SafetyAlertType } from '@casops/store/apis/commonModels';
import { useAppDispatch, useAppSelector } from '@casops/store/hooks';
import { AcknowledgeStatusFilter } from './components/acknowledge-status-filter/AcknowledgeStatusFilter';

type Model = SafetyAlertBasicModel;

const sidePanel = SidePanel.SafetyAlert;

export interface SafetyAlertsProps {
  alertType: SafetyAlertType;
}

export function SafetyAlerts({ alertType }: SafetyAlertsProps) {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>('-');

  const modelName =
    alertType === SafetyAlertType.Duress
      ? ModelName.SafetyAlertDuress
      : ModelName.SafetyAlertManDown;

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'ts', headerName: t('Fields.DateAndTime'), ...absoluteTimeColDef },
      { field: 'did', headerName: t('Fields.PPUID') },
      {
        field: 'ack',
        headerName: t('Fields.AlertStatus'),
        getQuickFilterText: ({ value }) => getSafetyAlertStatusText(Boolean(value), t),
        cellRenderer: SafetyAlertAcknowledgedCellRenderer,
      },
      { field: 'ackAt', headerName: t('Fields.AckAt'), ...absoluteTimeColDef },
      { field: 'ackBy.n', headerName: t('Fields.AckBy') },
    ];

    return columnDefs;
  }, [absoluteTimeColDef, t]);

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { duressStatus, manDownStatus } = useAppSelector(state => state.filters.saved);
  const statusFilter = alertType === SafetyAlertType.Duress ? duressStatus : manDownStatus;

  const { currentData, data, ...requestRemainder } = useListSafetyAlertsQuery(
    {
      safetyAlertType: alertType,
      safetyAlertAcknowledged: statusFilter,
    },
    { refetchOnMountOrArgChange: 120 },
  );

  const { items, countAll, countFilter } = currentData ?? {};

  const { hasData, noDataDescription } = useTableData<Model>({
    data: items,
    ...requestRemainder,
    noResultsDescription,
  });

  const onRowClick = useCallback(
    (data: Model) => {
      dispatch(
        openSidePanel({
          sidePanel: sidePanel,
          modelId: data.id.toString(),
          editMode: true, // The side panel contains a readonly form, but it must use editMode=true to know to load the model data.
        }),
      );
    },
    [dispatch],
  );

  return (
    <CollectionView<Model>
      key={alertType}
      sliceKey={modelName}
      modelName={modelName}
      columnDefs={columnDefs}
      rowData={items}
      hasData={hasData}
      noDataDescription={noDataDescription}
      onRowClick={onRowClick}
      // All filterProps are optional, so don't provide a filterGroup to just get the summary text.
      filterProps={{
        countAll,
        countFilter,
      }}
      actions={<AcknowledgeStatusFilter alertType={alertType} />}
    />
  );
}

export default SafetyAlerts;
